import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Drawer, Select } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import Link from 'antd/es/typography/Link';
import moment from 'moment';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

export interface FilterValues {
  clientName: string[];
  buName: { label: string; value: string }[];
  startDate: string | null;
  endDate: string | null;
}

interface OboFilterDrawerProps {
  open: boolean;
  onClose: () => void;
  onApply: (filters: FilterValues) => void;
  clientList: string[];
  buList: { label: string; value: string }[];
  initialValues: FilterValues;
  defaultValues: FilterValues;
}

const OboFilterDrawer = ({
  open,
  onClose,
  onApply,
  clientList,
  buList,
  initialValues,
  defaultValues,
}: OboFilterDrawerProps) => {
  const [selectedClient, setSelectedClient] = useState<string[]>(initialValues.clientName);
  const [selectedBu, setSelectedBu] = useState<{ label: string; value: string }[]>(initialValues.buName);
  const [startDate, setStartDate] = useState<string | null>(initialValues.startDate);
  const [endDate, setEndDate] = useState<string | null>(initialValues.endDate);

  const [resetBannerVisible, setResetBannerVisible] = useState(false);

  // Sync state when drawer re-opens
  useEffect(() => {
    if (open) {
      setSelectedClient(initialValues.clientName);
      setSelectedBu(initialValues.buName);
      setStartDate(initialValues.startDate);
      setEndDate(initialValues.endDate);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Put selected items at top of list, without duplicates
  const orderedList = (list: string[], selected: string[]) => {
    const rest = list.filter((item) => !selected.includes(item));
    return [...selected, ...rest.sort((a, b) => a.localeCompare(b))];
  };

  const orderedBuList = (list: { label: string; value: string }[], selected: { label: string; value: string }[]) => {
    const selectedValues = selected.map((s) => s.value);
    const rest = list.filter((item) => !selectedValues.includes(item.value));
    return [...selected, ...rest.sort((a, b) => a.label.localeCompare(b.label))];
  };

  const getKeyValueForBU = (valueList: string[]) => {
    const result: { label: string; value: string }[] = [];
    valueList.forEach((v) => {
      const found = buList.find((bu) => bu.value === v);
      if (found) result.push(found);
    });
    return result;
  };

  const handleDateChange = (_: any, dateString: string | string[]) => {
    const arr = dateString as string[];
    if (arr[0] && arr[1]) {
      const startParts = arr[0].split('-');
      const endParts = arr[1].split('-');
      const startFormatted = `${startParts[0]}-${startParts[1]}-01`;
      const endFormatted = moment(`${endParts[0]}-${endParts[1]}-01`).endOf('month').format('YYYY-MM-DD');
      setStartDate(startFormatted);
      setEndDate(endFormatted);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const isApplyDisabled = selectedClient.length === 0 || startDate === null || endDate === null;

  const handleApply = () => {
    onApply({ clientName: selectedClient, buName: selectedBu, startDate, endDate });
    onClose();
  };

  const handleReset = () => {
    setSelectedClient(defaultValues.clientName);
    setSelectedBu(defaultValues.buName);
    setStartDate(defaultValues.startDate);
    setEndDate(defaultValues.endDate);
    setResetBannerVisible(true);
    setTimeout(() => setResetBannerVisible(false), 3000);
  };

  return (
    <Drawer
      title={<span style={{ color: '#034EA2', fontWeight: 700 }}>Dashboard Filters</span>}
      placement="right"
      width={420}
      open={open}
      onClose={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={handleReset}>Reset All</Button>
          <Button
            type="primary"
            onClick={handleApply}
            disabled={isApplyDisabled}
            style={isApplyDisabled ? {} : { backgroundColor: '#034EA2' }}
          >
            Apply Filters
          </Button>
        </div>
      }
    >
      {resetBannerVisible && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          backgroundColor: '#f0fdf4', border: '1px solid #86efac',
          borderRadius: 6, padding: '8px 12px', marginBottom: 16,
          color: '#16a34a', fontSize: 13, fontWeight: 500,
        }}>
          <CheckCircleFilled style={{ fontSize: 14, flexShrink: 0 }} />
          <span>Filters reset to default. Click <strong>Apply Filters</strong> to load data.</span>
        </div>
      )}

      {/* ── Client Name ── */}
      <div className="obo-v2-drawer-field">
        <div className="obo-v2-drawer-label">Client Name <span style={{ color: '#E30773' }}>*</span></div>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Select client(s)"
          optionFilterProp="children"
          value={selectedClient}
          maxTagCount="responsive"
          onChange={(value: string[]) => setSelectedClient(value)}
          filterOption={(input: any, option: any) =>
            option.value.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          options={orderedList(clientList, selectedClient).map((item) => ({ value: item, label: item }))}
          dropdownRender={(menu) => (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-around', margin: '5px' }}>
                <Link onClick={() => setSelectedClient(clientList)} disabled={selectedClient.length === clientList.length}>
                  Select All
                </Link>
                <Link onClick={() => setSelectedClient([])} disabled={selectedClient.length === 0}>
                  Clear All
                </Link>
              </div>
              {menu}
            </>
          )}
        />
      </div>

      {/* ── Business Unit ── */}
      <div className="obo-v2-drawer-field">
        <div className="obo-v2-drawer-label">Business Unit</div>
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder="Select business unit(s)"
          optionFilterProp="children"
          value={selectedBu.map((item) => item.value)}
          maxTagCount="responsive"
          onChange={(value: string[]) => setSelectedBu(getKeyValueForBU(value))}
          filterOption={(input: any, option: any) =>
            option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0
          }
          options={orderedBuList(buList, selectedBu).map((item) => ({ value: item.value, label: item.label }))}
          dropdownRender={(menu) => (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-around', margin: '5px' }}>
                <Link onClick={() => setSelectedBu(buList)} disabled={selectedBu.length === buList.length}>
                  Select All
                </Link>
                <Link onClick={() => setSelectedBu([])} disabled={selectedBu.length === 0}>
                  Clear All
                </Link>
              </div>
              {menu}
            </>
          )}
        />
      </div>

      {/* ── Date Range ── */}
      <div className="obo-v2-drawer-field">
        <div className="obo-v2-drawer-label">
          Date Range <span style={{ color: '#E30773' }}>*</span>
        </div>
        <div className="obo-v2-drawer-sublabel">Select month range (start month → end month)</div>
        <RangePicker
          style={{ width: '100%' }}
          format="YYYY-MM"
          picker="month"
          onChange={handleDateChange as any}
          disabledDate={(current: any) =>
            current && (current > moment().endOf('month') || current < moment('2024-04-13').startOf('month'))
          }
          value={
            startDate && endDate
              ? [dayjs(startDate), dayjs(endDate)]
              : null
          }
        />
      </div>
    </Drawer>
  );
};

export default OboFilterDrawer;
